import { useState } from 'react'
import type { Contact } from '../../types'
import { useDataStore } from '../../stores/dataStore'
import { Button, Input, Select, Textarea, ConfirmModal } from '../ui'
import { toast } from '../ui/Toast'
import ActivityFeed from '../timeline/ActivityFeed'
import TaskList from '../timeline/TaskList'
import { EmailHistory, ComposeButton } from '../gmail'

interface ContactPanelProps {
  contact: Contact
  onClose: () => void
}

export default function ContactPanel({ contact, onClose }: ContactPanelProps) {
  const { companies, updateContact, deleteContact } = useDataStore()
  const [isEditing, setIsEditing] = useState(false)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)

  const [name, setName] = useState(contact.name)
  const [email, setEmail] = useState(contact.email || '')
  const [phone, setPhone] = useState(contact.phone || '')
  const [role, setRole] = useState(contact.role || '')
  const [companyId, setCompanyId] = useState(contact.companyId || '')
  const [notes, setNotes] = useState(contact.notes || '')

  const company = companies.find((c) => c.id === contact.companyId)

  const handleSave = async () => {
    if (!name.trim()) {
      toast('Name is required', 'error')
      return
    }

    try {
      await updateContact(contact.id, {
        name: name.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        role: role.trim() || undefined,
        companyId: companyId || undefined,
        notes: notes.trim() || undefined,
      })
      setIsEditing(false)
      toast('Contact updated', 'success')
    } catch {
      toast('Failed to update contact', 'error')
    }
  }

  const handleDelete = async () => {
    try {
      await deleteContact(contact.id)
      toast('Contact deleted', 'info')
      onClose()
    } catch {
      toast('Failed to delete contact', 'error')
    }
  }

  const companyOptions = [
    { value: '', label: 'No company' },
    ...companies.map((c) => ({ value: c.id, label: c.name })),
  ]

  return (
    <>
      <aside className="w-80 bg-bg-surface border border-border rounded-lg flex flex-col shadow-card">
        <header className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h2 className="font-semibold text-text-primary truncate">
            {isEditing ? 'Edit contact' : contact.name}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-[#F5F5F3] rounded-[6px] transition-colors duration-150"
            aria-label="Close panel"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </header>

        <div className="flex-1 p-4 overflow-y-auto">
          {isEditing ? (
            <div className="space-y-4">
              <Input
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Input
                label="Phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <Input
                label="Role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              />
              <Select
                label="Company"
                options={companyOptions}
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
              />
              <Textarea
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          ) : (
            <div className="space-y-4">
              {contact.email && (
                <div>
                  <p className="text-xs text-text-secondary mb-1">Email</p>
                  <a
                    href={`mailto:${contact.email}`}
                    className="text-sm text-action-primary hover:underline"
                  >
                    {contact.email}
                  </a>
                </div>
              )}

              {contact.phone && (
                <div>
                  <p className="text-xs text-text-secondary mb-1">Phone</p>
                  <a
                    href={`tel:${contact.phone}`}
                    className="text-sm text-action-primary hover:underline"
                  >
                    {contact.phone}
                  </a>
                </div>
              )}

              {contact.role && (
                <div>
                  <p className="text-xs text-text-secondary mb-1">Role</p>
                  <p className="text-sm">{contact.role}</p>
                </div>
              )}

              {company && (
                <div>
                  <p className="text-xs text-text-secondary mb-1">Company</p>
                  <p className="text-sm">{company.name}</p>
                </div>
              )}

              {contact.notes && (
                <div>
                  <p className="text-xs text-text-secondary mb-1">Notes</p>
                  <p className="text-sm whitespace-pre-wrap">{contact.notes}</p>
                </div>
              )}

              {contact.email && (
                <div className="pt-4">
                  <ComposeButton contact={contact} variant="primary" size="sm" />
                </div>
              )}

              <div className="pt-4 border-t border-border">
                <TaskList entityType="contact" entityId={contact.id} />
              </div>

              <div className="pt-4 border-t border-border">
                <ActivityFeed entityType="contact" entityId={contact.id} />
              </div>

              {contact.email && (
                <div className="pt-4 border-t border-border">
                  <EmailHistory contactEmail={contact.email} />
                </div>
              )}
            </div>
          )}
        </div>

        <footer className="px-4 py-3 border-t border-border flex justify-between">
          {isEditing ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button variant="primary" size="sm" onClick={handleSave}>
                Save
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setIsDeleteConfirmOpen(true)}
              >
                Delete
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            </>
          )}
        </footer>
      </aside>

      <ConfirmModal
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Delete contact"
        message="This will permanently delete this contact. This action cannot be undone."
        confirmText="Delete"
        variant="danger"
      />
    </>
  )
}
